import React, { useState, useEffect, useRef } from "react";
import Question from "./Question";
import { motion, AnimatePresence } from "framer-motion";

const FormTemplate = ({ questions, onSubmit, answers, setAnswers }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for next, -1 for prev
  const questionHistoryRef = useRef([]); // Track question IDs we've visited
  const visibleQuestionsRef = useRef([]); // Store stable reference to visible questions

  // Filter questions based on conditional logic
  const getVisibleQuestions = (currentAnswers = answers) => {
    return questions.filter((question) => {
      // If question has a condition, check it
      if (question.condition) {
        const { dependsOn, value: conditionValue } = question.condition;
        const answerValue = currentAnswers[dependsOn];
        // Support both single value and array of values
        if (Array.isArray(conditionValue)) {
          return conditionValue.includes(answerValue);
        }
        return answerValue === conditionValue;
      }
      // Show question if no condition
      return true;
    });
  };

  const visibleQuestions = getVisibleQuestions();

  // Track previous visible questions to detect major changes
  const prevVisibleQuestionsRef = useRef(
    visibleQuestions.map((q) => q.id).join(",")
  );

  // Update ref when visible questions change
  useEffect(() => {
    visibleQuestionsRef.current = visibleQuestions;
    const currentIds = visibleQuestions.map((q) => q.id).join(",");
    const prevIds = prevVisibleQuestionsRef.current;

    // If the question IDs have changed significantly (like when cardType changes),
    // reset to first question
    if (prevIds !== currentIds && prevIds !== "") {
      // Check if current question still exists in new list
      const currentQuestion = visibleQuestions[currentQuestionIndex];
      if (!currentQuestion) {
        setCurrentQuestionIndex(0);
      }
    }
    prevVisibleQuestionsRef.current = currentIds;
  }, [visibleQuestions, currentQuestionIndex]);

  // Reset to first question if visible questions change significantly
  useEffect(() => {
    // Ensure currentQuestionIndex is always valid
    if (currentQuestionIndex >= visibleQuestions.length) {
      setCurrentQuestionIndex(0);
      questionHistoryRef.current = [];
    }
    // If current question doesn't exist, go to first available
    if (
      visibleQuestions.length > 0 &&
      !visibleQuestions[currentQuestionIndex]
    ) {
      setCurrentQuestionIndex(0);
    }
  }, [visibleQuestions.length, currentQuestionIndex, visibleQuestions]);

  const handleChange = (questionId, answer) => {
    console.log("=== HANDLE CHANGE ===");
    console.log("questionId:", questionId);
    console.log("answer:", answer);
    
    // Create new answers object immediately to calculate next step
    const newAnswers = { ...answers, [questionId]: answer };
    
    setAnswers(newAnswers);

    // Auto-advance logic moved here
    const currentQuestion = visibleQuestions[currentQuestionIndex];
    if (currentQuestion?.autoAdvance && currentQuestion.type === "choice" && answer) {
      // Track this question in history
      if (!questionHistoryRef.current.includes(currentQuestion.id)) {
        questionHistoryRef.current.push(currentQuestion.id);
      }

      // Get fresh visible questions based on NEW answers
      const freshVisibleQuestions = getVisibleQuestions(newAnswers);

      // Find the next valid question index
      let nextIndex = currentQuestionIndex + 1;
      while (
        nextIndex < freshVisibleQuestions.length &&
        !freshVisibleQuestions[nextIndex]
      ) {
        nextIndex++;
      }

      if (nextIndex < freshVisibleQuestions.length) {
        setDirection(1);
        // Small delay to allow user to see selection
        setTimeout(() => {
          setCurrentQuestionIndex(nextIndex);
          visibleQuestionsRef.current = freshVisibleQuestions;
        }, 200);
      }
    }
  };

  const validateAnswers = () => {
    // Check required questions
    for (let question of visibleQuestions) {
      if (question.required === "true" || question.required === true) {
        const answer = answers[question.id];

        if (question.type === "open" || question.type === "tel") {
          if (!answer || answer.trim() === "") {
            return {
              valid: false,
              error: `Please answer: ${question.text}`,
              questionId: question.id,
            };
          }
        } else if (question.type === "choice" || question.type === "close") {
          if (!answer) {
            return {
              valid: false,
              error: `Please answer: ${question.text}`,
              questionId: question.id,
            };
          }
        }
      }
    }
    return { valid: true };
  };

  const handleNext = () => {
    if (currentQuestionIndex < visibleQuestions.length - 1) {
      setDirection(1);
      setCurrentQuestionIndex((prev) => {
        const nextIndex = prev + 1;
        const nextQuestion = visibleQuestions[nextIndex];
        if (
          nextQuestion &&
          !questionHistoryRef.current.includes(nextQuestion.id)
        ) {
          questionHistoryRef.current.push(nextQuestion.id);
        }
        return nextIndex;
      });
    } else {
      // Validate before submitting
      const validation = validateAnswers();
      if (validation.valid) {
        console.log("=== FORM SUBMIT: CALLING onSubmit ===");
        console.log(
          "answers being submitted:",
          JSON.stringify(answers, null, 2)
        );
        onSubmit(answers); // Submit the form
      } else {
        // Find and jump to the question with error
        const errorIndex = visibleQuestions.findIndex(
          (q) => q.id === validation.questionId
        );
        if (errorIndex !== -1) {
          setCurrentQuestionIndex(errorIndex);
          alert(validation.error);
        }
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setDirection(-1);
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const currentQuestion = visibleQuestions[currentQuestionIndex];

  if (!currentQuestion) {
    return <div>Loading...</div>;
  }

  // Animation variants
  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction > 0 ? -50 : 50,
      opacity: 0,
    }),
  };

  return (
    <div className="w-full md:w-2/3 lg:w-full lg:ml-8 items-center justify-center flex flex-col">
      {/* Question container */}
      <div className="w-full relative" style={{ minHeight: "300px" }}>
        <AnimatePresence mode="wait" custom={direction}>
          {currentQuestion && (
            <motion.div
              key={currentQuestion.id}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
              className="w-full"
            >
              <Question
                question={currentQuestion}
                value={answers[currentQuestion.id] || ""}
                onChange={handleChange}
                answers={answers}
                className=""
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Fixed button container - always in same position */}
      <div className="flex flex-row gap-6 ml-1 mt-6 text-blue-900 font-medium text-1xl md:ml-4 w-full">
        <button
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
          className={`disabled:opacity-30 py-2 px-4 rounded-md  text-gray-600 outline outline-1 ${
            currentQuestionIndex !== 0
              ? " hover:outline-black hover:outline-2 outline-gray-600 hover:text-black transition-all"
              : " "
          }`}
        >
          Previous
        </button>
        <button
          onClick={handleNext}
          className="bg-blue-700 hover:bg-blue-900 text-white py-2 px-4 rounded-md"
        >
          {currentQuestionIndex === visibleQuestions.length - 1
            ? "Submit"
            : "Continue"}
        </button>
      </div>
    </div>
  );
};

export default FormTemplate;
